// Cart API Service
// API client for shopping cart operations
// Updated: 2026-03-24 Round 3 - Ensure auth_token is used consistently

import type {
  CartResponse,
  AddToCartRequest,
} from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://bonbalayelo-api-preview.bonbalayelo.workers.dev';

/**
 * Helper function to get auth token
 */
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

/**
 * Helper function to make authenticated API calls
 */
async function authenticatedFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const token = getAuthToken();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Unknown error' } }));
    throw new Error(error.error?.message || 'API request failed');
  }

  return response.json();
}

/**
 * Cart API Service
 */
export const cartApi = {
  /**
   * Get current user's cart
   */
  async getCart(): Promise<CartResponse> {
    return authenticatedFetch<CartResponse>('/api/cart', {
      method: 'GET',
    });
  },

  /**
   * Add item to cart
   * @param request - Add to cart request with productId, variantId, variantAttributes, quantity
   */
  async addToCart(request: AddToCartRequest): Promise<CartResponse> {
    return authenticatedFetch<CartResponse>('/api/cart/items', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * Update item quantity in cart
   * @param itemId - Cart item ID
   * @param quantity - New quantity (0 or greater)
   */
  async updateItemQuantity(itemId: string, quantity: number): Promise<CartResponse> {
    return authenticatedFetch<CartResponse>(`/api/cart/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  },

  /**
   * Remove item from cart
   * @param itemId - Cart item ID to remove
   */
  async removeItem(itemId: string): Promise<CartResponse> {
    return authenticatedFetch<CartResponse>(`/api/cart/items/${itemId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Clear entire cart
   */
  async clearCart(): Promise<CartResponse> {
    return authenticatedFetch<CartResponse>('/api/cart', {
      method: 'DELETE',
    });
  },

  /**
   * Sync cart (recalculate prices and check stock)
   */
  async syncCart(): Promise<CartResponse> {
    return authenticatedFetch<CartResponse>('/api/cart/sync', {
      method: 'POST',
    });
  },
};
